import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CreditCard, Check, ExternalLink, Loader2, AlertCircle, XCircle } from "lucide-react";

interface MollieMethod {
  id: string;
  description: string;
  image?: string;
}

interface PaymentStatus {
  has_payment_method: boolean;
  setup_status: "none" | "pending" | "active" | "failed";
  selected_methods: string[];
  enabled_methods: string[];
}

// Fallback labels if API description is missing
const METHOD_LABELS: Record<string, string> = {
  creditcard: "Kreditkarte",
  directdebit: "SEPA-Lastschrift",
  sofort: "Sofortüberweisung",
  applepay: "Apple Pay",
  googlepay: "Google Pay",
  banktransfer: "Banküberweisung",
  ideal: "iDEAL",
  eps: "EPS",
  giropay: "Giropay",
  billie: "Billie (Rechnungskauf)",
};

async function callMollieApi(accessToken: string, action: string, params?: any) {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mollie-api`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ action, ...params }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Anfrage fehlgeschlagen");
  return data;
}

export default function PaymentSetup() {
  const { profile } = useProvider();
  const queryClient = useQueryClient();
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);

  // Fetch payment status
  const { data: paymentStatus, isLoading: isLoadingStatus, refetch: refetchStatus } = useQuery({
    queryKey: ["mollie-payment-status", profile?.id],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Nicht eingeloggt");
      return callMollieApi(session.access_token, "get_status") as Promise<PaymentStatus>;
    },
    enabled: !!profile?.id,
    refetchInterval: 15_000,
  });

  // Fetch available methods
  const { data: methodsData } = useQuery({
    queryKey: ["mollie-methods", profile?.id],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Nicht eingeloggt");
      return callMollieApi(session.access_token, "get_methods");
    },
    enabled: !!profile?.id,
  });

  const enabledMethods: MollieMethod[] = methodsData?.enabled_methods || [];

  // Initialize selected methods from status
  useEffect(() => {
    if (paymentStatus?.selected_methods?.length) {
      setSelectedMethods(paymentStatus.selected_methods);
    } else if (enabledMethods.length > 0) {
      setSelectedMethods(enabledMethods.map(m => m.id));
    }
  }, [paymentStatus, enabledMethods]);

  // Handle return from Mollie
  useEffect(() => {
    const url = new URL(window.location.href);
    const setupReturn = url.searchParams.get("setup");
    if (setupReturn === "return") {
      url.searchParams.delete("setup");
      window.history.replaceState({}, "", url.pathname);
      // Poll more aggressively for a bit
      const interval = setInterval(() => refetchStatus(), 3000);
      setTimeout(() => clearInterval(interval), 30000);
      refetchStatus();
    }
  }, []);

  // Start setup mutation
  const startSetup = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Nicht eingeloggt");

      const returnUrl = `${window.location.origin}${window.location.pathname}?setup=return`;
      const data = await callMollieApi(session.access_token, "setup_start", {
        selected_methods: selectedMethods,
        return_url: returnUrl,
      });

      window.location.href = data.checkout_url;
    },
    onError: (err: Error) => {
      toast.error(err.message || "Fehler beim Starten des Setups");
    },
  });

  const toggleMethod = (methodId: string) => {
    setSelectedMethods(prev =>
      prev.includes(methodId)
        ? prev.filter(id => id !== methodId)
        : [...prev, methodId]
    );
  };

  if (!profile) {
    return (
      <div className="border border-border bg-card p-8 text-center space-y-2">
        <h2 className="text-headline">Kein Provider-Profil</h2>
        <p className="text-caption">Zahlungseinrichtung steht nur für Provider-Accounts zur Verfügung.</p>
      </div>
    );
  }

  if (isLoadingStatus) {
    return (
      <div className="border border-border bg-card p-8 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  const hasPayment = paymentStatus?.has_payment_method;
  const setupStatus = paymentStatus?.setup_status || "none";

  // ── Active state ──
  if (hasPayment && setupStatus === "active") {
    return (
      <div className="border border-border bg-card p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
            <Check className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-headline">Zahlungsmittel aktiv</h2>
            <p className="text-caption">Ihr Konto ist für automatische Abrechnung freigeschaltet.</p>
          </div>
        </div>
        <div className="divider-line" />
        {paymentStatus?.selected_methods?.length > 0 && (
          <div>
            <span className="text-micro block mb-2">Aktive Methoden</span>
            <div className="flex flex-wrap gap-2">
              {paymentStatus.selected_methods.map((m) => (
                <span key={m} className="text-xs px-3 py-1.5 border border-accent/30 bg-accent/5 text-accent">
                  {METHOD_LABELS[m] || m}
                </span>
              ))}
            </div>
          </div>
        )}
        <Button
          variant="quiet"
          size="sm"
          onClick={() => startSetup.mutate()}
          disabled={startSetup.isPending}
        >
          {startSetup.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird vorbereitet…</>
          ) : (
            "Zahlungsmittel ändern"
          )}
        </Button>
      </div>
    );
  }

  // ── Pending state ──
  if (setupStatus === "pending") {
    return (
      <div className="border border-border bg-card p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: "hsl(var(--accent))" }} />
          </div>
          <div>
            <h2 className="text-headline">Warten auf Bestätigung</h2>
            <p className="text-caption">Ihre Zahlung wird verarbeitet. Dies kann einige Minuten dauern.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Setup form (none, failed) ──
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="text-center space-y-4 py-8">
        <div className="w-16 h-16 mx-auto rounded-full bg-accent/10 flex items-center justify-center">
          <CreditCard className="w-8 h-8 text-accent" />
        </div>
        <h1 className="text-headline">Zahlungsmittel hinterlegen</h1>

        {setupStatus === "failed" && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 text-sm leading-relaxed flex gap-3 text-left max-w-md mx-auto">
            <XCircle className="w-5 h-5 shrink-0 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">Einrichtung fehlgeschlagen</p>
              <p className="text-caption mt-1">Bitte versuchen Sie es erneut.</p>
            </div>
          </div>
        )}

        <p className="text-caption max-w-md mx-auto">
          Bitte hinterlegen Sie ein Zahlungsmittel, damit Anfragen freigeschaltet werden können.
        </p>
        <p className="text-caption max-w-md mx-auto font-medium">
          Kein Abo · Keine Provision · Abbuchung nur bei Annahme einer Anfrage · Keine weiteren oder versteckten Kosten.
        </p>
      </div>

      {/* Method selection */}
      {enabledMethods.length > 0 && (
        <div className="border border-border bg-card p-8 space-y-6">
          <h3 className="text-subheadline">Zahlungsmethode wählen</h3>
          <div className="space-y-3">
            {enabledMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => toggleMethod(method.id)}
                className={`w-full text-left p-4 border transition-all duration-200 flex items-center gap-4 ${
                  selectedMethods.includes(method.id)
                    ? "border-accent bg-accent/5"
                    : "border-border hover:border-muted-foreground"
                }`}
              >
                {method.image && (
                  <img src={method.image} alt={method.description} className="w-8 h-8 object-contain" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium">{method.description || METHOD_LABELS[method.id] || method.id}</p>
                </div>
                {selectedMethods.includes(method.id) && (
                  <div className="w-2 h-2 bg-accent rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border bg-card p-8 space-y-6">
        <h3 className="text-subheadline">So funktioniert es</h3>
        <div className="space-y-4">
          <div className="flex gap-4 items-start">
            <span className="w-8 h-8 shrink-0 rounded-full bg-accent/10 flex items-center justify-center text-sm font-medium text-accent">1</span>
            <div>
              <p className="text-sm font-medium">Sichere Checkout-Seite</p>
              <p className="text-caption">Sie werden auf die sichere Zahlungsseite von Mollie weitergeleitet.</p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <span className="w-8 h-8 shrink-0 rounded-full bg-accent/10 flex items-center justify-center text-sm font-medium text-accent">2</span>
            <div>
              <p className="text-sm font-medium">Zahlungsmittel bestätigen</p>
              <p className="text-caption">Geben Sie Ihre Zahlungsdaten ein und bestätigen Sie.</p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <span className="w-8 h-8 shrink-0 rounded-full bg-accent/10 flex items-center justify-center text-sm font-medium text-accent">3</span>
            <div>
              <p className="text-sm font-medium">Automatische Freischaltung</p>
              <p className="text-caption">Nach Bestätigung werden Anfragen automatisch freigeschaltet.</p>
            </div>
          </div>
        </div>

        <div className="divider-line" />

        <div className="p-4 bg-secondary/30 border border-border text-sm leading-relaxed flex gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 text-muted-foreground mt-0.5" />
          <p className="text-caption">
            Ihre Zahlungsdaten werden nicht bei CRAFTFINDUS gespeichert, sondern sicher bei Mollie verwaltet.
            Mollie ist PCI-DSS Level 1 zertifiziert.
          </p>
        </div>
      </div>

      <Button
        variant="commit"
        size="xl"
        className="w-full"
        onClick={() => startSetup.mutate()}
        disabled={startSetup.isPending || selectedMethods.length === 0}
      >
        {startSetup.isPending ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird vorbereitet…</>
        ) : (
          <><ExternalLink className="w-4 h-4 mr-2" />Zahlungsmittel hinterlegen</>
        )}
      </Button>
    </div>
  );
}
